import { AnimatePresence, motion } from 'framer-motion';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import {
    FaCheckCircle,
    FaChevronDown,
    FaChevronUp,
    FaClock,
    FaExclamationCircle,
    FaExclamationTriangle,
    FaPlay,
    FaSpinner
} from 'react-icons/fa';
import { JobRunHistory, NightlyJobConfigModel } from '../../_core/_model.ts';
import { GetNightlyJobConfig, GetNightlyJobHistory, TriggerNightlyJob, UpdateNightlyJobConfig } from '../../_core/_request.ts';
import moment from 'moment';
import { getMyLocalTime } from '../../Utilities/Utilities.ts';
// import { useToast } from '../../context/ToastContext.tsx'; // Ensure this matches your project context

// Extended interface locally to ensure jobStatus is typed (if not already in your model)
interface ExtendedNightlyJobConfig extends NightlyJobConfigModel {
    jobStatus?: string;
}

export default function NightlyJobConfig() {
    const [config, setConfig] = useState<ExtendedNightlyJobConfig>({ isJobEnabled: false, scheduledTime: "01:30", jobStatus: "idle" });
    const [initialConfig, setInitialConfig] = useState<ExtendedNightlyJobConfig>({ isJobEnabled: false, scheduledTime: "01:30" });
    const [history, setHistory] = useState<JobRunHistory[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [expandedRun, setExpandedRun] = useState<string | null>(null);

    // Track previous status to detect when a job transitions from running -> completed
    const previousStatusRef = useRef<string | undefined>("idle");

    // Fallback toast if context isn't hooked up yet
    const toast = { success: console.log, error: console.error };

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            await fetchHistoryOnly();
            const configRes = await GetNightlyJobConfig();
            setConfig(configRes.data);
            setInitialConfig(configRes.data);
            previousStatusRef.current = configRes.data.jobStatus;
        } catch (error) {
            console.warn("API failed, loading injected dummy data for Nightly Job.", error);
            loadDummyData();
        } finally {
            setLoading(false);
        }
    };

    const fetchHistoryOnly = async () => {
        try {
            const historyRes = await GetNightlyJobHistory();
            setHistory(historyRes.data);
        } catch (error) {
            console.error("Failed to fetch history updates.", error);
        }
    };

    const loadDummyData = () => {
        const dummyConfig = { isJobEnabled: true, scheduledTime: "01:30", jobStatus: "idle" };
        setConfig(dummyConfig);
        setInitialConfig(dummyConfig);

        setHistory([
            {
                "durationMinutes": 1,
                "endTime": "04:14 AM",
                "runDate": "2026-03-12",
                "runID": "RUN-202603120413",
                "stages": [
                    { "durationMinutes": 0, "endTime": "04:13 AM", "stageID": "S1", "stageName": "Fetch Roles", "startTime": "04:13 AM", "status": "Success" },
                    { "durationMinutes": 0, "endTime": "04:13 AM", "stageID": "S2", "stageName": "Execute Role Conditions & Get SIDs", "startTime": "04:13 AM", "status": "Success" },
                    { "durationMinutes": 0, "endTime": "04:13 AM", "stageID": "S3", "stageName": "Fetch Users", "startTime": "04:13 AM", "status": "Success" },
                    { "durationMinutes": 1, "endTime": "04:14 AM", "stageID": "S4", "stageName": "Update SIDs per Assigned Roles", "startTime": "04:13 AM", "status": "Success" }
                ],
                "startTime": "04:13 AM",
                "status": "Success"
            },
            {
                "durationMinutes": 1,
                "endTime": "04:10 AM",
                "runDate": "2026-03-12",
                "runID": "RUN-202603120409",
                "stages": [
                    { "durationMinutes": 0, "endTime": "04:09 AM", "stageID": "S1", "stageName": "Fetch Roles", "startTime": "04:09 AM", "status": "Success" },
                    { "durationMinutes": 0, "endTime": "04:09 AM", "stageID": "S2", "stageName": "Execute Role Conditions & Get SIDs", "startTime": "04:09 AM", "status": "Success" },
                    { "durationMinutes": 0, "endTime": "04:09 AM", "stageID": "S3", "stageName": "Fetch Users", "startTime": "04:09 AM", "status": "Success" },
                    { "durationMinutes": 1, "endTime": "04:10 AM", "stageID": "S4", "stageName": "Update SIDs per Assigned Roles", "startTime": "04:09 AM", "status": "Success" }
                ],
                "startTime": "04:09 AM",
                "status": "Success"
            }
        ]);
    };

    // --- Core Job Status Management ---

    const isJobRunning = useCallback((status?: string) => {
        if (!status) return false;
        const s = status.toLowerCase();
        return s.includes('start') || s.includes('run') || s.includes('pending') || s.includes('progress');
    }, []);

    const formatJobStatus = (status?: string) => {
        if (!status) return "Idle";
        // Convert "snowflake.snowpark.started" -> "Snowpark Started"
        let formatted = status.replace(/snowflake\.snowpark\.?/gi, 'Snowpark ');
        // Capitalize each word for premium UI
        return formatted.replace(/\b\w/g, c => c.toUpperCase()).trim();
    };

    // Polling Effect
    useEffect(() => {
        let interval: NodeJS.Timeout;

        const pollJobStatus = async () => {
            try {
                const configRes = await GetNightlyJobConfig();
                const newStatus = configRes.data.jobStatus || "idle";

                setConfig(prev => ({ ...prev, jobStatus: newStatus }));

                // Check if job just finished
                if (!isJobRunning(newStatus) && isJobRunning(previousStatusRef.current)) {
                    toast?.success("Nightly Job completed. Fetching latest history...");
                    fetchHistoryOnly();
                }
                previousStatusRef.current = newStatus;

            } catch (error) {
                console.warn("Polling failed.", error);
            }
        };

        if (isJobRunning(config.jobStatus)) {
            // Poll every 5 seconds if running
            interval = setInterval(pollJobStatus, 5000);
        }

        return () => clearInterval(interval);
    }, [config.jobStatus, isJobRunning, toast]);


    const handleSaveConfig = async () => {
        setSaving(true);
        try {
            await UpdateNightlyJobConfig(config);
            toast?.success("Nightly Job configuration saved successfully.");
            setInitialConfig(config);
        } catch (error) {
            console.warn("Failed to save config remotely, updating local state.");
            toast?.success("Config updated locally (API Mock).");
            setInitialConfig(config);
        } finally {
            setSaving(false);
        }
    };

    const handleRunNow = async () => {
        // Optimistic UI Update to lock the button and start polling immediately
        setConfig(prev => ({ ...prev, jobStatus: 'started' }));
        previousStatusRef.current = 'started';

        try {
            await TriggerNightlyJob();
            toast?.success("Nightly Job triggered manually. Monitoring progress...");
        } catch (error) {
            toast?.success("Manual trigger simulated.");
            // Simulate a job completion for dummy data environments after 8 seconds
            setTimeout(() => {
                setConfig(prev => ({ ...prev, jobStatus: 'success' }));
                fetchHistoryOnly(); // Refresh History UI
            }, 8000);
        }
    };

    const getStatusConfig = (statusStr: string) => {
        const s = (statusStr || "").toLowerCase();
        if (s.includes('success') && !s.includes('partial')) return { color: "text-success", bg: "bg-success", icon: <FaCheckCircle /> };
        if (s.includes('fail') || s.includes('error')) return { color: "text-danger", bg: "bg-danger", icon: <FaExclamationCircle /> };
        if (s.includes('partial')) return { color: "text-warning", bg: "bg-warning", icon: <FaExclamationTriangle /> };
        return { color: "text-secondary", bg: "bg-secondary", icon: <FaClock /> };
    };

    if (loading) return <div className="d-flex justify-content-center p-5"><Spinner animation="border" variant="primary" /></div>;

    // We only care if settings are dirty, ignoring jobStatus changes from polling
    const isConfigDirty = config.isJobEnabled !== initialConfig.isJobEnabled || config.scheduledTime !== initialConfig.scheduledTime;
    const isRunning = isJobRunning(config.jobStatus);

    return (
        <div className="d-flex flex-column gap-4">
            {/* Configuration Header Area */}
            <div className="d-flex flex-wrap justify-content-between align-items-center bg-theme-card border rounded p-3 shadow-sm position-relative overflow-hidden">
                {/* Subtle animated background if running */}
                {isRunning && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 0.05 }}
                        className="position-absolute top-0 start-0 w-100 h-100 bg-primary"
                        style={{ zIndex: 0, pointerEvents: 'none' }}
                    />
                )}

                <div style={{ zIndex: 1 }}>
                    <div className="d-flex align-items-center gap-3 mb-1">
                        <h5 className="mb-0 fw-bold d-flex align-items-center gap-2">
                            Nightly RLS Sync Job
                        </h5>

                        {/* Premium Live Status Badge */}
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={config.jobStatus}
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                className={`d-flex align-items-center gap-2 px-3 py-1 rounded-pill fw-bold shadow-sm ${isRunning ? 'bg-primary text-white' :
                                    config.jobStatus?.toLowerCase().includes('fail') ? 'bg-danger text-white' :
                                        config.jobStatus?.toLowerCase().includes('success') ? 'bg-success text-white' :
                                            'bg-light border text-secondary'
                                    }`}
                                style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}
                            >
                                {isRunning && <Spinner animation="grow" size="sm" style={{ width: '12px', height: '12px' }} />}
                                {!isRunning && config.jobStatus?.toLowerCase().includes('success') && <FaCheckCircle />}
                                {formatJobStatus(config.jobStatus)}
                            </motion.div>
                        </AnimatePresence>
                    </div>
                    <p className="text-muted mb-0 mt-2" style={{ fontSize: '0.85rem' }}>Generates User RLS entries daily by executing role conditions and fetching SIDs.</p>
                </div>

                <div className="d-flex align-items-center gap-4 mt-3 mt-md-0" style={{ zIndex: 1 }}>
                    <div className="d-flex align-items-center gap-2">
                        <label className="fw-semibold small mb-0 text-muted">Scheduled Time:</label>
                        <input
                            type="time"
                            className="form-control form-control-sm text-muted bg-light border-light"
                            value={config.scheduledTime}
                            disabled
                            readOnly
                            style={{ cursor: 'not-allowed' }}
                        />
                    </div>

                    <div className="form-check form-switch d-flex align-items-center gap-2 mb-0" style={{ transform: 'scale(1.1)', transformOrigin: 'left center' }}>
                        <input
                            className={`form-switch form-check-input ${config.isJobEnabled ? 'bg-success border-success' : 'bg-secondary border-secondary'}`}
                            type="checkbox"
                            role="switch"
                            id="enable-job-switch"
                            checked={config.isJobEnabled}
                            onChange={(e) => setConfig({ ...config, isJobEnabled: e.target.checked })}
                            disabled={isRunning}
                            style={{ cursor: isRunning ? 'not-allowed' : 'pointer', height: '1.2rem', width: '2.4rem' }}
                        />
                    </div>

                    <div className="d-flex gap-2">
                        <AnimatePresence>
                            {isConfigDirty && !isRunning && (
                                <motion.button
                                    initial={{ opacity: 0, scale: 0.8, width: 0 }}
                                    animate={{ opacity: 1, scale: 1, width: 'auto' }}
                                    exit={{ opacity: 0, scale: 0.8, width: 0 }}
                                    className="btn btn-sm btn-success d-flex align-items-center justify-content-center gap-1 overflow-hidden text-nowrap shadow-sm"
                                    onClick={handleSaveConfig}
                                    disabled={saving}
                                >
                                    {saving ? <Spinner size="sm" animation="border" /> : 'Save'}
                                </motion.button>
                            )}
                        </AnimatePresence>

                        <button
                            className={`btn btn-sm d-flex align-items-center gap-2 shadow-sm ${isRunning ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={handleRunNow}
                            disabled={isRunning}
                            title="Trigger job immediately"
                        >
                            {isRunning ? <FaSpinner className="fa-spin" size={12} /> : <FaPlay size={10} />}
                            {isRunning ? 'Running...' : 'Run Now'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Run History Area */}
            <div>
                <h6 className="fw-bold mb-3 border-bottom pb-2">Recent Job Runs</h6>
                <div className="d-flex flex-column gap-2">
                    {history.length === 0 && !isRunning && (
                        <div className="text-muted text-center p-4 bg-light rounded border border-dashed">
                            No recent history available.
                        </div>
                    )}

                    {history.map((run) => {
                        const statusConfig = getStatusConfig(run.status);
                        const isExpanded = expandedRun === run.runID;

                        return (
                            <div key={run.runID} className="card shadow-sm border-0">
                                <div
                                    className="card-body p-3 d-flex justify-content-between align-items-center"
                                    style={{ cursor: 'pointer', backgroundColor: 'var(--bs-card-cap-bg, rgba(0,0,0,0.02))' }}
                                    onClick={() => setExpandedRun(isExpanded ? null : run.runID)}
                                >
                                    <div className="d-flex align-items-center gap-3">
                                        <div className={`fs-5 ${statusConfig.color}`}>
                                            {statusConfig.icon}
                                        </div>
                                        <div>
                                            <div className="fw-bold fs-6">{getMyLocalTime(run.runDate)}</div>
                                            <div className="text-muted small">Started: {moment.tz(run.startTime, 'America/Los_Angeles').local().format("h:mm A")} • Duration: {run.durationMinutes} mins</div>
                                        </div>
                                    </div>
                                    <div className="d-flex align-items-center gap-3">
                                        <span className={`badge ${statusConfig.bg} bg-opacity-75 text-white px-3 py-2 rounded-pill`}>
                                            {run.status}
                                        </span>
                                        <div className="text-muted">
                                            {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                                        </div>
                                    </div>
                                </div>

                                <AnimatePresence>
                                    {isExpanded && (
                                        <motion.div
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: 'auto', opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            transition={{ duration: 0.2 }}
                                            style={{ overflow: 'hidden' }}
                                        >
                                            <div className="card-body border-top bg-body">
                                                <div className="d-flex flex-column position-relative" style={{ paddingLeft: '1.5rem' }}>
                                                    <div className="position-absolute h-100 border-start" style={{ left: '0.75rem', top: '1rem', zIndex: 0 }}></div>

                                                    {run.stages.map((stage) => {
                                                        const stageCfg = getStatusConfig(stage.status);
                                                        return (
                                                            <div key={stage.stageID} className="position-relative mb-3 pb-2 w-100" style={{ zIndex: 1 }}>
                                                                <div className="d-flex align-items-start gap-3">
                                                                    <div className={`bg-white rounded-circle ${stageCfg.color} d-flex justify-content-center align-items-center`} style={{ width: '20px', height: '20px', transform: 'translateX(-22px)', marginTop: '4px' }}>
                                                                        {stageCfg.icon}
                                                                    </div>
                                                                    <div className="flex-grow-1" style={{ transform: 'translateX(-16px)' }}>
                                                                        <div className="d-flex justify-content-between align-items-center">
                                                                            <span className="fw-semibold">{stage.stageName}</span>
                                                                            <span className="text-muted small">
                                                                                {stage.status.toLowerCase().includes('progress') ? 'Skipped/Pending' : `${stage.durationMinutes} min`}
                                                                            </span>
                                                                        </div>
                                                                        <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                                                                            {stage.startTime !== '--' && `${moment.tz(stage.startTime, 'America/Los_Angeles').local().format("h:mm A")} - ${moment.tz(stage.endTime, 'America/Los_Angeles').local().format("h:mm A")}`}
                                                                        </div>

                                                                        {stage.errorMessage && (
                                                                            <div className="alert alert-danger mt-2 py-2 px-3 mb-0" style={{ fontSize: '0.8rem' }}>
                                                                                <FaExclamationTriangle className="me-2" />
                                                                                {stage.errorMessage}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )
                                                    })}
                                                </div>
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        )
                    })}
                </div>
            </div>
        </div>
    );
}